import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Subject, firstValueFrom, forkJoin, of } from 'rxjs';
import { bufferTime, catchError, concatMap, filter } from 'rxjs/operators';

import { environment } from '../../environments/environment.js';

interface BatchTileResponse {
  layer: string;
  sas_tokens: Record<string, string[]>;
}

/**
 * Batches internal vector tile SAS-token requests into a single backend call
 * every 100ms, mirroring the batching used by the 2D map viewer so both apps
 * share the same server-side contract (GET {apiUrl}/map/tiles/batch).
 */
@Injectable({ providedIn: 'root' })
export class VectorTileBatchService {
  private http = inject(HttpClient);

  private readonly tileQueue$ = new Subject<{ layer: string; identifier: string }>();
  private readonly resolveMap = new Map<string, (value: string[]) => void>();

  constructor() {
    const tenant = new URLSearchParams(window.location.search).get('tenant') ?? '';
    this.tileQueue$
      .pipe(
        bufferTime(100),
        filter(items => items.length > 0),
        concatMap(items => {
          const layers = [...new Set(items.map(item => item.layer))];
          const requests = layers.map(layer => {
            const identifiers = items
              .filter(item => item.layer === layer)
              .map(item => item.identifier);
            return this.http
              .get<BatchTileResponse>(`${environment.apiUrl}/v1/map/tiles/batch`, {
                params: { layer, identifiers, tenant },
              })
              .pipe(
                catchError(error => {
                  console.error(`Error fetching vector tile batch for layer "${layer}":`, error);
                  const sas_tokens: Record<string, string[]> = {};
                  for (const identifier of identifiers) sas_tokens[identifier] = [];
                  return of<BatchTileResponse>({ layer, sas_tokens });
                }),
              );
          });
          return forkJoin(requests);
        }),
      )
      .subscribe(responses => {
        for (const response of responses) {
          for (const identifier in response.sas_tokens) {
            const key = `${response.layer}/${identifier}`;
            const resolve = this.resolveMap.get(key);
            if (resolve) {
              resolve(response.sas_tokens[identifier]);
              this.resolveMap.delete(key);
            }
          }
        }
      });
  }

  /**
   * Resolve SAS urls for a single {layer}/{z}/{x}/{y} tile and download it.
   * Multiple shards can back one tile; each is fetched independently so the
   * caller can merge their features.
   */
  requestTile = async (layer: string, z: number, x: number, y: number): Promise<Blob[]> => {
    const identifier = `${z}/${x}/${y}`;
    const key = `${layer}/${identifier}`;

    const sasUrls = await new Promise<string[]>(resolve => {
      this.resolveMap.set(key, resolve);
      this.tileQueue$.next({ layer, identifier });
    });

    const results = await Promise.allSettled(sasUrls.map(url => this.fetchBlob(url)));
    return results.map(result => (result.status === 'fulfilled' ? result.value : new Blob()));
  };

  private fetchBlob(url: string): Promise<Blob> {
    return firstValueFrom(this.http.get(url, { responseType: 'blob' }));
  }
}
